import { useCallback } from 'react'

import { useResourcesApi } from '@/services/api/resourcesApi'
import { InstitutionOption, InstitutionSap } from '@/types/forms/'

const fieldsMain = ['educationModality', 'admissionType']
const fieldsSchoolLocation = [
    'countrySchool',
    'departmentSchool',
    'municipalitySchool'
]
const fieldsUniversityLocation = [
    'universityCountry',
    'universityDepartment',
    'universityMunicipality'
]
const fieldsUniversity = [
    'universityName',
    'universityTitle',
    'universityStudyStatus',
    'examType',
    'examResult'
]
const fieldsSchool = [
    'schoolName',
    'titleSchool',
    'schoolStudyStatus',
    'isIcfesReceived',
    'icfesUniqueId',
    'icfesType',
    'icfesExamDate',
    'icfesScores'
]
const fieldsExternal = ['educationStatus', 'graduationDate']

const initialExcludedFields: string[] = [
    ...fieldsMain,
    ...fieldsSchoolLocation,
    ...fieldsUniversityLocation,
    ...fieldsUniversity,
    ...fieldsSchool,
    ...fieldsExternal
]

const mappedInstitution = (institution: InstitutionSap): InstitutionOption => ({
    value: institution.idObj || institution.id?.toString() || '',
    label: institution.denominacionObjeto || '',
    code: institution.idObj || '',
    country: institution.denominacionPais || '',
    city: institution.poblacion || '',
    department: institution.denominacionDepartamento || ''
})

export function EducationFunctions() {
    const resourcesApi = useResourcesApi()

    const getInstitutions = useCallback(
        async (
            institutionName: string,
            api: boolean
        ): Promise<InstitutionOption[] | undefined> => {
            if (institutionName.length >= 6) {
                let response: InstitutionSap[] = []

                try {
                    // Buscar por los primeros 6+ caracteres del término de búsqueda
                    const searchForApi = institutionName.substring(0, 6)

                    if (api) {
                        response =
                            await resourcesApi.getSchoolsByName(searchForApi)
                    } else {
                        response =
                            await resourcesApi.getUniversitiesByName(
                                searchForApi
                            )
                    }

                    if (Array.isArray(response)) {
                        const searchTerm = institutionName.toLowerCase().trim()

                        // Filtrar por nombre, ciudad o departamento con el término completo
                        const filtered = response.filter((institution) => {
                            const name = institution.denominacionObjeto
                                ?.toLowerCase()
                                .trim()
                            const city = institution.poblacion
                                ?.toLowerCase()
                                .trim()
                            const department =
                                institution.denominacionDepartamento
                                    ?.toLowerCase()
                                    .trim()

                            return (
                                name?.includes(searchTerm) ||
                                city?.includes(searchTerm) ||
                                department?.includes(searchTerm)
                            )
                        })

                        const mappedOptions = filtered
                            .slice(0, 50) // Limitar a 50 resultados
                            .map(mappedInstitution)

                        return mappedOptions
                    }
                } catch (error) {
                    console.error('Error fetching institutions:', error)
                }
            } else {
                return []
            }
        },
        [resourcesApi]
    )

    const excludeFields = (
        modality: number,
        admissionType: number
    ): { external: boolean; completeExcludeFields: string[] } => {
        let currentExcludedFields = [...initialExcludedFields]

        if (modality === 15 || modality === 16) {
            currentExcludedFields = currentExcludedFields.filter(
                (field) =>
                    !fieldsSchool.includes(field) &&
                    !fieldsSchoolLocation.includes(field)
            )
        } else {
            currentExcludedFields = currentExcludedFields.filter(
                (field) =>
                    !fieldsUniversity.includes(field) &&
                    !fieldsUniversityLocation.includes(field)
            )
        }

        if (admissionType === 1) {
            return {
                external: true,
                completeExcludeFields: currentExcludedFields.filter(
                    (field) => !fieldsExternal.includes(field)
                )
            }
        } else {
            return {
                external: false,
                completeExcludeFields: currentExcludedFields
            }
        }
    }

    const getEducationOptions = async () => {
        const [bachillerTitlesRes, universityTitlesRes, studyStatusRes] =
            await Promise.all([
                resourcesApi.getBachillerTitles(),
                resourcesApi.getSapUniversityTitles(),
                resourcesApi.getStudyStatus()
            ])

        const bachillerTitles = bachillerTitlesRes || []
        const universityTitles = universityTitlesRes || []

        // Handle study status response structure
        const studyStatusData = (studyStatusRes as any)?.data || []
        const studyStatus = studyStatusData.map((item: any) => ({
            label: item.nombre,
            value: item.valor
        }))

        return {
            schoolTitles: bachillerTitles,
            universityTitles,
            studyStatusOptions: studyStatus
        }
    }

    return {
        getInstitutions,
        excludeFields,
        getEducationOptions
    }
}
